import { element } from 'prop-types';
import React from 'react';
import {
  Notification,
  ScaledSceneRenderer,
  VideoPlayerProvider
} from '@shoflo/polymer';
import { CAMERA_DISCONNECTIVITY_MESSAGE } from '@studio/constants/Views';
import { useSelector } from 'react-redux';
import { shouldHaveFeature } from '@studio/feature.flags/featureFlags';

import {
  ScaledSceneRendererWrapper,
  SceneLayoutWrapper,
  SceneRendererWrapper,
  SourceListSticky,
  SourceListWrapper,
  StyledAlertNotificationWrapper,
  StyledCountdownTimerWrapper,
  StyledFooter,
  StyledFooterWrapper,
  StyledHeader,
  StyledHeaderLeft,
  StyledHeaderWrapper,
  StyledMediaPlaybackController,
  StyledMediaSelectWrapper,
  StyledNameTagsToggleWrapper,
  StyledNotificationButtonWrapper,
  StyledSceneLayoutWrapper,
  StyledSceneWrapper,
  StyledSourceListWrapper,
  StyledStage,
  StyledStageLabel,
  StyledStageLabelWrapper,
  StyledManageStreamButtonWrapper,
  StyledStreamButtonWrapper,
  VideoNotificationAlertWrapper,
  Wrapper
} from './Layout.styled';
import { useLayoutDimensions } from 'hooks';

function Layout({
  chatWidget,
  sourceList,
  manageStreamButton,
  streamButton,
  sceneRenderer,
  nameTagsToggle,
  notificationAlert,
  notificationButton,
  mediaControl,
  multiSelectControl,
  audioOutput,
  statusPane,
  countdownTimer,
  streamNotification,
  presentationLayoutToggle
}) {
  const videoDeviceLost = useSelector(state => state.videoDeviceLost);
  const { stageWidth, ratio } = useLayoutDimensions();

  // this is to check if the CountdownTimer has been passed
  // todo remove this once the feature is fully enabled
  const hasCountdownTimer = !!countdownTimer;

  // check for multicast feature flag
  const isMulticastEnabled = shouldHaveFeature('studio.multicast');
  return (
    <Wrapper data-testid="layout-wrapper">
      {audioOutput}
      <StyledHeaderWrapper>
        <StyledHeaderLeft
          data-testid="layout-header-left"
          $hasCountdownTimer={hasCountdownTimer}
        >
          <StyledCountdownTimerWrapper data-testid="countdown-timer-wrapper">
            {countdownTimer}
          </StyledCountdownTimerWrapper>
        </StyledHeaderLeft>
        <StyledHeader
          md={{ size: 10 }}
          data-testid="layout-header"
          $hasCountdownTimer={hasCountdownTimer}
        >
          {isMulticastEnabled && (
            <StyledManageStreamButtonWrapper>
              {manageStreamButton}
            </StyledManageStreamButtonWrapper>
          )}
          <StyledStreamButtonWrapper data-testid="stream-button-wrapper">
            {streamButton}
          </StyledStreamButtonWrapper>
          <StyledNotificationButtonWrapper data-testid="notification-wrapper">
            {notificationButton}
          </StyledNotificationButtonWrapper>
        </StyledHeader>
      </StyledHeaderWrapper>
      {videoDeviceLost && (
        <VideoNotificationAlertWrapper>
          <Notification type="alert" message={CAMERA_DISCONNECTIVITY_MESSAGE} />
        </VideoNotificationAlertWrapper>
      )}
      <StyledAlertNotificationWrapper data-testid="alert-notification-wrapper">
        {notificationAlert}
        {streamNotification}
      </StyledAlertNotificationWrapper>
      <StyledStage>
        <div>
          <VideoPlayerProvider>
            <StyledSceneWrapper data-testid="scene-wrapper">
              <SceneRendererWrapper
                id="stage"
                data-testid="scene-renderer-wrapper"
              >
                <StyledStageLabelWrapper stageWidth={stageWidth}>
                  <StyledStageLabel>Stage</StyledStageLabel>
                </StyledStageLabelWrapper>
                <ScaledSceneRendererWrapper data-testid="scaled-scene-renderer-wrapper">
                  <ScaledSceneRenderer ratio={ratio} pageWidth={stageWidth}>
                    {sceneRenderer}
                  </ScaledSceneRenderer>
                </ScaledSceneRendererWrapper>
                <StyledMediaPlaybackController stageWidth={stageWidth}>
                  {mediaControl}
                </StyledMediaPlaybackController>
              </SceneRendererWrapper>
            </StyledSceneWrapper>
          </VideoPlayerProvider>
          <StyledSceneLayoutWrapper
            data-testid="scene-layout-wrapper"
            stageWidth={stageWidth}
          >
            <SceneLayoutWrapper>
              <StyledMediaSelectWrapper>
                {multiSelectControl}
              </StyledMediaSelectWrapper>
              {presentationLayoutToggle}
              <StyledNameTagsToggleWrapper data-testid="name-tags-toggle-wrapper">
                {nameTagsToggle}
              </StyledNameTagsToggleWrapper>
            </SceneLayoutWrapper>
          </StyledSceneLayoutWrapper>
        </div>
      </StyledStage>
      <SourceListSticky>
        <StyledSourceListWrapper
          fluid="xl"
          data-testid="styled-source-list-wrapper"
        >
          <SourceListWrapper
            id="source-list-wrapper-div"
            data-testid="source-list-wrapper"
          >
            {sourceList}
          </SourceListWrapper>
        </StyledSourceListWrapper>
        <StyledFooterWrapper>
          <StyledFooter md={{ size: 10 }} data-testid="layout-footer">
            {chatWidget}
          </StyledFooter>
        </StyledFooterWrapper>
      </SourceListSticky>
      {statusPane}
    </Wrapper>
  );
}

Layout.propTypes = {
  chatWidget: element,
  sourceList: element,
  streamButton: element,
  mediaControl: element,
  sceneRenderer: element,
  nameTagsToggle: element,
  notificationAlert: element,
  notificationButton: element,
  sceneLayoutChooser: element,
  multiSelectControl: element,
  audioOutput: element,
  statusPane: element,
  presentationControl: element,
  countdownTimer: element,
  streamNotification: element
};

export default Layout;
